import { DrawableEntity, DrawProduct, MeasureSettings, View } from "@novorender/api";
import { ReadonlyVec2, vec2, vec3 } from "gl-matrix";
import { MutableRefObject, useCallback, useEffect, useRef, useState } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { Canvas2D } from "components";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { CameraState, drawPart, drawProduct, getCameraState, vec3Sum } from "features/engine2D";
import { processDrawProductTextTemplates } from "features/uom/draw";
import { useUom } from "features/uom/hooks/useUom";
import { selectIsProcoreDesign } from "slices/explorer/selectors";
import { ExtendedMeasureEntity } from "types/misc";
import { hasMouseSupport } from "utils/misc";

import { measureInteractionPositionsRef } from "./measureInteractionPositionsRef";
import { ActiveAxis, selectMeasure } from "./measureSlice";
import { useMeasureObjects } from "./useMeasureObjects";
import { useMove2DInteractions } from "./useMove2DInteractions";

const colors = {
    fill: "rgba(0, 191, 255, 0.15)",
    point: "rgba(0, 191, 255, 0.75)",
    line: "rgba(255, 255, 0, 1)",
};

const procoreColors = {
    fill: "rgba(0, 219, 44, 0.15)",
    point: "rgba(0, 219, 44, 0.75)",
    line: "rgba(0, 219, 44, 1)",
};

type RemoveAxisMap = Map<"x" | "y" | "z" | "plan" | "dist" | "normal", vec2>;

type DrawProductData = {
    product: DrawProduct;
    isGenerated: boolean;
};

export function MeasureCanvas({
    onMeasureUpdate,
    renderFnRef,
    svg,
}: {
    onMeasureUpdate?: () => void;
    renderFnRef: MutableRefObject<((moved: boolean) => void) | undefined>;
    svg: SVGSVGElement | null;
}) {
    const {
        state: { size, view },
    } = useExplorerGlobals();
    const [canvas, setCanvas] = useState<HTMLCanvasElement | null>(null);
    const [ctx, setCtx] = useState<CanvasRenderingContext2D | null | undefined>(null);

    const measureSets = useMeasureObjects();
    const measure = useAppSelector(selectMeasure);
    const uom = useUom();
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);

    const moveInteractionMarkers = useMove2DInteractions(svg, measureInteractionPositionsRef);

    const drawProductCacheRef = useRef(new Map<string, DrawProductData | undefined>());
    const resultsDrawProductCacheRef = useRef(
        new Map<number, { product: DrawProduct | undefined; activeAxis: ActiveAxis; isGenerated?: boolean }>(),
    );

    const updateId = useRef(0);
    const update = useCallback(async () => {
        if (!view?.measure || !ctx || !canvas) {
            return false;
        }

        const id = ++updateId.current;
        const drawProductCache = new Map<string, DrawProductData | undefined>();
        const resultsDrawProductCache = new Map<
            number,
            { product: DrawProduct | undefined; activeAxis: ActiveAxis; isGenerated?: boolean }
        >();

        const areas: Record<number, number | undefined> = {};
        await Promise.all(
            measureSets.flatMap((set, setIdx) =>
                set.map(async (entity, entityIdx) => {
                    if (entity && entity.drawKind === "face" && view.measure?.core) {
                        const measureValues = await view.measure.core.measure(entity, undefined, entity.settings);
                        if (
                            measureValues &&
                            "kind" in measureValues &&
                            (measureValues.kind === "plane" || measureValues.kind === "polymesh")
                        ) {
                            const key = setIdx * 100 + entityIdx;
                            areas[key] = measureValues.area;
                        }
                    }
                }),
            ),
        );

        if (id !== updateId.current) {
            return false;
        }

        const [updatedSets, updatedResults] = await Promise.all([
            Promise.all(
                measureSets.map(async (set) =>
                    Promise.all(
                        set.map(async (obj) => {
                            if (obj.drawKind === "vertex") {
                                const product = await getDrawMeasureEntity(view, obj);
                                if (product) {
                                    processDrawProductTextTemplates(uom, product);
                                }
                                const isGenerated = Boolean(
                                    product ? await view.measure?.core.isParametricDataGenerated(obj.ObjectId) : false,
                                );
                                const productData = product ? { product, isGenerated } : undefined;
                                drawProductCache.set("-1", productData);
                                return productData;
                            }

                            const objId = toId(obj);
                            const cached = drawProductCacheRef.current.get(objId);

                            if (cached) {
                                view.measure?.draw.updateProduct(cached.product);
                                processDrawProductTextTemplates(uom, cached.product);
                                drawProductCache.set(objId, cached);
                                return cached;
                            } else if (!cached) {
                                const product = await getDrawMeasureEntity(view, obj, {
                                    ...obj.settings,
                                    segmentLabelInterval: 10,
                                });
                                if (product) {
                                    processDrawProductTextTemplates(uom, product);
                                }
                                const isGenerated = Boolean(
                                    product ? await view.measure?.core.isParametricDataGenerated(obj.ObjectId) : false,
                                );
                                const productData = product ? { product, isGenerated } : undefined;
                                drawProductCache.set(objId, productData);
                                return productData;
                            }
                        }),
                    ),
                ),
            ),
            Promise.all(
                measure.duoMeasurementValues.map(async (measurement, i) => {
                    const activeAxis = measure.activeAxis[i];

                    if (!activeAxis || !measurement?.result) {
                        return;
                    }

                    const cached = resultsDrawProductCacheRef.current.get(measurement.id);
                    if (cached && cached.product) {
                        view.measure?.draw.updateProduct(cached.product);
                        cached.activeAxis = activeAxis;
                        resultsDrawProductCache.set(measurement.id, {
                            product: cached.product,
                            activeAxis: cached.activeAxis,
                            isGenerated: cached.isGenerated,
                        });
                        return { product: cached.product, activeAxis };
                    } else if (!cached) {
                        const product = await getDrawMeasureEntity(view, measurement.result, measurement.settings);
                        if (product) {
                            processDrawProductTextTemplates(uom, product);
                        }
                        resultsDrawProductCache.set(measurement.id, {
                            product,
                            activeAxis,
                            isGenerated: false,
                        });
                        return { product, activeAxis };
                    }
                }),
            ),
        ]);

        if (id !== updateId.current) {
            return false;
        }

        drawProductCacheRef.current = drawProductCache;
        resultsDrawProductCacheRef.current = resultsDrawProductCache;

        const removePos: (vec2 | undefined)[] = [];
        const infoPos: (vec2 | undefined)[] = [];
        const surfaceInfo: Record<number, { center: vec2; area: number | undefined } | undefined> = {};
        updatedSets.forEach((_set, i) => {
            const set = _set.filter((prod): prod is DrawProductData => prod !== undefined);

            const measureSet = measureSets[i];
            const hasSurfaces = measureSet && measureSet.some((entity) => entity && entity.drawKind === "face");

            if (hasSurfaces) {
                set.forEach((product, entityIdx) => {
                    if (!product) {
                        return;
                    }

                    product.product.objects.forEach(function setInteractionPositions({ kind, parts }) {
                        // polymeshes get "unknown" kind. Check that the last part is filled instead
                        const isPlaneLike =
                            kind === "plane" || (kind === "unknown" && parts.at(-1)?.drawType === "filled");
                        if (!isPlaneLike) {
                            return;
                        }

                        const obj = parts.at(-1);
                        if (!obj) {
                            return;
                        }

                        const sum = vec3Sum(obj.vertices3D.slice(0, obj.vertices3D.length - 1));
                        const pos3d = vec3.scale(vec3.create(), sum, 1 / (obj.vertices3D.length - 1));
                        if (vec3.dist(pos3d, view.renderState.camera.position) > 100) {
                            return;
                        }

                        const sp = view.convert.worldSpaceToScreenSpace([pos3d]);
                        if (sp && sp.length > 0 && sp[0]) {
                            const key = i * 100 + entityIdx;
                            surfaceInfo[key] = { center: sp[0], area: areas[key] };

                            if (entityIdx === 0) {
                                removePos[i] = sp[0];
                                infoPos[i] = vec2.fromValues(sp[0][0] + 25, sp[0][1]);
                            }
                        }
                    });
                });
                return;
            }

            const attachInteractions = set.length === 1 && set[0];
            if (!attachInteractions) {
                return;
            }

            attachInteractions.product.objects.forEach(function setInteractionPositions({ kind, parts }) {
                switch (kind) {
                    case "plane": {
                        const obj = parts.at(-1);

                        if (!obj) {
                            return;
                        }

                        const sum = vec3Sum(obj.vertices3D.slice(0, obj.vertices3D.length - 1));
                        const pos3d = vec3.scale(vec3.create(), sum, 1 / (obj.vertices3D.length - 1));
                        if (vec3.dist(pos3d, view.renderState.camera.position) > 100) {
                            return;
                        }

                        const sp = view.measure?.draw.toMarkerPoints([pos3d]);
                        if (sp && sp.length > 0 && sp[0]) {
                            removePos[i] = sp[0];
                            infoPos[i] = vec2.fromValues(sp[0][0] + 25, sp[0][1]);
                        } else {
                            removePos[i] = undefined;
                        }

                        return;
                    }
                    case "cylinder":
                    case "curveSegment":
                    case "edge": {
                        const vertices2D = parts[0] && parts[0].vertices2D;

                        if (!vertices2D) {
                            return;
                        }

                        const [start, end] =
                            vertices2D[0][0] > vertices2D[vertices2D.length - 1][0]
                                ? [vertices2D[0], vertices2D[vertices2D.length - 1], 1, -1]
                                : [vertices2D[vertices2D.length - 1], vertices2D[0], 1, 1];

                        const dir = vec2.sub(vec2.create(), end, start);
                        const dist = vec2.len(dir);
                        const offset = vec2.fromValues((dir[1] / dist) * 20, (-dir[0] / dist) * 20);
                        if (dist > 110) {
                            removePos[i] = vec2.add(
                                vec2.create(),
                                vec2.scaleAndAdd(vec2.create(), start, dir, 0.5),
                                offset,
                            );
                            infoPos[i] = vec2.add(
                                vec2.create(),
                                vec2.scaleAndAdd(vec2.create(), start, dir, (dist / 2 + 25) / dist),
                                offset,
                            );
                        }

                        return;
                    }
                    case "vertex": {
                        const pt = parts[0] && parts[0].vertices2D && parts[0].vertices2D[0];

                        if (pt) {
                            removePos[i] = vec2.fromValues(pt[0] + 25, pt[1]);
                        }

                        return;
                    }
                    default:
                        return;
                }
            });
        });

        const removeAxis: typeof measureInteractionPositionsRef.current.removeAxis = [];
        updatedResults.forEach((result, i) => {
            if (!result?.activeAxis || !result.product || !result.product.objects[0]) {
                return;
            }

            const activeAxis = result.activeAxis;
            const axes = result.product.objects[0].parts.reduce(
                (axes, { vertices2D, name }) => {
                    if (!vertices2D || vertices2D.length < 2) {
                        return axes;
                    }

                    const dir = vec2.sub(vec2.create(), vertices2D[1], vertices2D[0]);
                    const dist = vec2.len(dir) || 1;
                    const axis = dist > 120 ? axes.long : !axes.short.size ? axes.short : undefined;

                    if (!axis) {
                        return axes;
                    }

                    if (axis === axes.short) {
                        axis.clear();
                    }

                    const removeOffset = (dist / 2 + 55) / dist;
                    const infoOffset = (dist / 2 + 80) / dist;

                    switch (name) {
                        case "result": {
                            if (activeAxis.result) {
                                axis.set("dist", vec2.scaleAndAdd(vec2.create(), vertices2D[0], dir, removeOffset));
                            }
                            infoPos[i] = vec2.scaleAndAdd(vec2.create(), vertices2D[0], dir, infoOffset);
                            break;
                        }
                        case "xy-plane": {
                            if (activeAxis.planar) {
                                axis.set("plan", vec2.scaleAndAdd(vec2.create(), vertices2D[0], dir, removeOffset));
                            }
                            break;
                        }
                        case "x-axis": {
                            if (activeAxis.x) {
                                axis.set("x", vec2.scaleAndAdd(vec2.create(), vertices2D[0], dir, removeOffset));
                            }
                            break;
                        }
                        case "y-axis": {
                            if (activeAxis.y) {
                                axis.set("y", vec2.scaleAndAdd(vec2.create(), vertices2D[0], dir, removeOffset));
                            }
                            break;
                        }
                        case "z-axis": {
                            if (activeAxis.z) {
                                axis.set("z", vec2.scaleAndAdd(vec2.create(), vertices2D[0], dir, removeOffset));
                            }
                            break;
                        }
                        case "normal": {
                            axis.set("normal", vec2.scaleAndAdd(vec2.create(), vertices2D[0], dir, removeOffset));
                        }
                    }

                    return axes;
                },
                { long: new Map() as RemoveAxisMap, short: new Map() as RemoveAxisMap },
            );

            removeAxis.push(Object.fromEntries(axes.long.size ? axes.long : axes.short));
        });

        measureInteractionPositionsRef.current.remove = removePos;
        measureInteractionPositionsRef.current.info = infoPos;
        measureInteractionPositionsRef.current.removeAxis = removeAxis;
        measureInteractionPositionsRef.current.surfaceInfo = surfaceInfo;

        return true;
    }, [view, canvas, ctx, measure.duoMeasurementValues, measureSets, measure.activeAxis, uom]);

    const isMobile = !hasMouseSupport();

    const draw = useCallback(() => {
        if (ctx && canvas && size && view) {
            const cameraState = getCameraState(view.renderState.camera);
            ctx.clearRect(0, 0, canvas.width, canvas.height);

            // On mobile, only show text labels for the currently selected set
            let currentEntityKeys: Set<string> | undefined;
            if (isMobile) {
                const currentEntities = measureSets[measure.currentIndex] || [];
                currentEntityKeys = new Set(currentEntities.map((e) => toId(e)));
            }

            drawMeasureObjects(drawProductCacheRef.current, ctx, cameraState, isProcoreDesign, currentEntityKeys);
            drawDuoResults(resultsDrawProductCacheRef.current, ctx, cameraState, isProcoreDesign);
        }
    }, [ctx, canvas, size, view, isProcoreDesign, isMobile, measureSets, measure.currentIndex]);

    useEffect(() => {
        update().then((updated) => {
            if (updated) {
                draw();
                moveInteractionMarkers();
                onMeasureUpdate?.();
            }
        });
    }, [update, draw, moveInteractionMarkers, onMeasureUpdate]);

    useEffect(() => {
        renderFnRef.current = animate;
        return () => (renderFnRef.current = undefined);
        async function animate(moved: boolean): Promise<void> {
            if (!view || !moved) {
                return;
            }

            const updated = await update();
            if (updated) {
                draw();
                moveInteractionMarkers();
                onMeasureUpdate?.();
            }
        }
    }, [draw, moveInteractionMarkers, onMeasureUpdate, renderFnRef, update, view]);

    const canDraw = measureSets.some((set) => set.length);

    return (
        canDraw && (
            <Canvas2D
                id="measure-canvas"
                data-include-snapshot
                ref={(el) => {
                    setCanvas(el);
                    setCtx(el?.getContext("2d") ?? null);
                }}
                width={size.width}
                height={size.height}
                sx={{ opacity: measure.hover ? 0.5 : 1 }}
            />
        )
    );
}

function toId(obj: ExtendedMeasureEntity) {
    return `${obj.ObjectId}_${obj.instanceIndex}_${obj.pathIndex}_${obj.settings?.cylinderMeasure}`;
}

async function getDrawMeasureEntity(view: View, entity?: DrawableEntity, settings?: MeasureSettings) {
    return entity && view.measure?.draw.getDrawEntity(entity, settings);
}

function drawMeasureObjects(
    products: Map<string, DrawProductData | undefined>,
    context2D: CanvasRenderingContext2D,
    camSettings: CameraState,
    isProcoreDesign: boolean,
    currentEntityKeys?: Set<string>,
) {
    const procoreTextSettings = {
        type: "default" as const,
        backgroundColor: "#000",
        fillStyle: "#FFF",
        strokeStyle: "transparent",
    };

    products.forEach((entry, key) => {
        if (entry) {
            const { product, isGenerated } = entry;
            const showText = !currentEntityKeys || currentEntityKeys.has(key);

            drawProduct(
                context2D,
                camSettings,
                product,
                {
                    lineColor: isProcoreDesign ? procoreColors.line : colors.line,
                    fillColor: isProcoreDesign ? procoreColors.fill : colors.fill,
                    pointColor: isProcoreDesign ? procoreColors.point : colors.point,
                    displayAllPoints: !isProcoreDesign,
                    complexCylinder: true,
                    lineDash: isGenerated && !isProcoreDesign ? [10, 10] : undefined,
                },
                3,
                showText
                    ? isProcoreDesign
                        ? procoreTextSettings
                        : { type: "default" as const, alwaysShow: true }
                    : undefined,
            );
        }
    });
}

function drawDuoResults(
    resultDraw: Map<number, { product: DrawProduct | undefined; activeAxis: ActiveAxis }>,
    context2D: CanvasRenderingContext2D,
    camSettings: CameraState,
    isProcoreDesign: boolean,
) {
    const textSettings = isProcoreDesign
        ? {
              type: "centerOfLine" as const,
              fillStyle: "#FFFFFF",
              strokeStyle: "#000000",
          }
        : {
              type: "centerOfLine" as const,
          };

    for (const [_id, { product, activeAxis }] of resultDraw) {
        if (product && product.objects.length === 1) {
            const obj = product.objects[0];
            const lines: [ReadonlyVec2, ReadonlyVec2][] = [];
            let cylinderAngleDrawn = false;
            for (const part of obj.parts) {
                if (part.vertices2D === undefined) {
                    continue;
                }
                let skip = false;
                if (part.vertices2D.length === 2) {
                    for (const l of lines) {
                        const a = vec2.dist(l[0], part.vertices2D[0]) < 10 && vec2.dist(l[1], part.vertices2D[1]) < 10;
                        const b = vec2.dist(l[0], part.vertices2D[1]) < 10 && vec2.dist(l[1], part.vertices2D[0]) < 10;
                        if (a || b) {
                            skip = true;
                        }
                    }
                    lines.push([part.vertices2D[0], part.vertices2D[1]]);
                }
                if (skip) {
                    continue;
                }

                switch (part.name) {
                    case "result":
                        if (activeAxis.result) {
                            drawPart(
                                context2D,
                                camSettings,
                                part,
                                {
                                    lineColor: "lightgreen",
                                    pointColor: { start: "green", middle: "white", end: "blue" },
                                    displayAllPoints: true,
                                },
                                3,
                                textSettings,
                            );
                        }
                        break;
                    case "normal":
                        drawPart(
                            context2D,
                            camSettings,
                            part,
                            { lineColor: "black", pointColor: "black", displayAllPoints: true },
                            3,
                            textSettings,
                        );
                        break;
                    case "x-axis":
                        if (activeAxis.x) {
                            drawPart(context2D, camSettings, part, { lineColor: "red" }, 3, textSettings);
                        }
                        break;
                    case "y-axis":
                        if (activeAxis.y) {
                            drawPart(context2D, camSettings, part, { lineColor: "blue" }, 3, textSettings);
                        }
                        break;
                    case "z-axis":
                        if (activeAxis.z) {
                            drawPart(context2D, camSettings, part, { lineColor: "green" }, 3, textSettings);
                        }
                        break;
                    case "xy-plane":
                        if (activeAxis.planar) {
                            drawPart(context2D, camSettings, part, { lineColor: "purple" }, 3, textSettings);
                        }
                        break;
                    case "z-angle":
                        if (activeAxis.z && activeAxis.result) {
                            drawPart(context2D, camSettings, part, { lineColor: "green" }, 2, textSettings);
                        }
                        break;
                    case "x-angle":
                        if (activeAxis.x && activeAxis.result) {
                            drawPart(context2D, camSettings, part, { lineColor: "red" }, 2, textSettings);
                        }
                        break;
                    case "y-angle":
                        if (activeAxis.y && activeAxis.result) {
                            drawPart(context2D, camSettings, part, { lineColor: "blue" }, 2, textSettings);
                        }
                        break;
                    case "xz-angle":
                        if (activeAxis.x && activeAxis.z) {
                            drawPart(context2D, camSettings, part, { lineColor: "purple" }, 2, textSettings);
                        }
                        break;
                    case "cylinder-angle":
                        cylinderAngleDrawn = drawPart(
                            context2D,
                            camSettings,
                            part,
                            { lineColor: "blue" },
                            2,
                            textSettings,
                        );
                        break;
                    case "cylinder-angle-line":
                        if (cylinderAngleDrawn) {
                            drawPart(context2D, camSettings, part, { lineColor: "blue" }, 2, textSettings);
                        }
                        break;
                }
            }
        }
    }
}
